'use client';
import React from 'react';

import { StockSymbol } from '@benzinga/session';
import { getQuoteChange, Quote } from '@benzinga/quotes-manager';
import Hooks from '@benzinga/hooks';
import { useQuoteSubscription } from '@benzinga/quotes-manager-hooks';

export type RenderSnapshot = (
  symbol: string,
  currentPrice: string | null,
  nominalChange: number | null,
  percentChange: number | null,
) => React.ReactElement;

interface OwnProps {
  symbol: StockSymbol;
  render: RenderSnapshot;
  getSnapshotSize?(
    symbol: string | null,
    currentPrice: string | null,
    nominalChange: number | null,
    percentChange: number | null,
  ): number;
  reflow?(size: number): void;
}

type Props = OwnProps;

const MiniSnapshot: React.FC<Props> = React.memo(
  props => {
    const { quote } = useQuoteSubscription(props.symbol);

    Hooks.useEffectDidUpdate<[Props, Quote | undefined]>(
      (prevProps, prevQuote) => {
        if (!props.getSnapshotSize || !props.reflow) {
          return;
        }

        const {
          change: prevChange = null,
          currentPriceFormatted: prevCurrentPriceFormatted = null,
          percentChange: prevPercentChange = null,
        } = prevQuote ?? {};
        const { symbol: prevSymbol } = prevProps;

        const { change = null, currentPriceFormatted = null, percentChange = null } = quote ?? {};
        const { symbol } = props;

        if (
          prevSymbol !== symbol ||
          prevChange !== change ||
          prevPercentChange !== percentChange ||
          prevCurrentPriceFormatted !== currentPriceFormatted
        ) {
          const prevSize = props.getSnapshotSize(prevSymbol, prevCurrentPriceFormatted, prevChange, prevPercentChange);
          const size = props.getSnapshotSize(symbol, currentPriceFormatted, change, percentChange);

          if (prevSize !== size) {
            props.reflow(size);
          }
        }
      },
      [props, quote],
    );

    const formatNumber = (num: string | null) => {
      if (num) return Number(num).toLocaleString('en-US');
      else return num;
    };

    const { currentPriceFormatted = null } = quote ?? {};
    const { nominalChange, percentageChange } = getQuoteChange<Quote>(quote);
    const { symbol } = props;

    return props.render(symbol, formatNumber(currentPriceFormatted), nominalChange, percentageChange);
  },
  (prevProps, nextProps) => prevProps.symbol === nextProps.symbol,
);

export default MiniSnapshot;
